import React from 'react';
import {useIntl} from 'react-intl';
import {Modal, Text} from '@forecast-it/design-system';

export interface NovaSaveVersionModalProps {
	// The name of the version being saved, as the author sees it.
	versionName: string;
	// How many companies run this version, or null when that could not be read.
	companyCount: number | null;
	onSave: () => void;
	onSaveAsNew: () => void;
	onClose: () => void;
}

// Asks before saving a version companies run, since the save goes live for them at once. Cancel keeps
// the edits unsaved; Save as new version leaves those companies where they are.
export function NovaSaveVersionModal({
	versionName,
	companyCount,
	onSave,
	onSaveAsNew,
	onClose,
}: NovaSaveVersionModalProps): JSX.Element {
	const {formatMessage} = useIntl();
	const choose = (action: () => void) => (): void => {
		onClose();
		action();
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={onClose}>
			<Modal.Title>{formatMessage({id: 'nova.saveVersion.title'}, {versionName})}</Modal.Title>
			<Modal.Content>
				<Text data-cy="nova-save-version-body">
					{companyCount === null
						? formatMessage({id: 'nova.saveVersion.goesLiveUnknown'})
						: formatMessage({id: 'nova.saveVersion.goesLive'}, {count: companyCount})}
				</Text>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={choose(onSaveAsNew)} dataCy="nova-save-version-as-new">
					{formatMessage({id: 'nova.saveVersion.saveAsNew'})}
				</Modal.SecondaryFooterButton>
				<Modal.TertiaryFooterButton onPress={onClose} dataCy="nova-save-version-cancel">
					{formatMessage({id: 'nova.saveVersion.cancel'})}
				</Modal.TertiaryFooterButton>
				<Modal.PrimaryFooterButton onPress={choose(onSave)} dataCy="nova-save-version-confirm">
					{formatMessage({id: 'nova.saveVersion.save'})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
